// CONTRACT:I4-POLICY-DECISION.1.0
/** actor — the PURE effective-authority cap of the ACTOR ACCESS MODEL
 *  (docs/AOA-ACTOR-ACCESS-MODEL.md / docs/adr/0008-actor-access-model.md).
 *
 *  This is the host-side generalization of the NPE `min()` currently inline in
 *  cop.ts (the "level you operate AT = min(your clearance, the NPE you talk
 *  through, ...)"). It is a PURE, deterministic, zero-dependency module so it can be
 *  proven in isolation (apps/cop/scripts/actor-check.ts) BEFORE it is wired into the
 *  live recompute paths — the existing lockstep oracle (apps/cop/scripts/lockstep.ts)
 *  feeds raw JWT claims straight to the PDP and does NOT exercise the host cap, so
 *  the cap needs its own gate. Wiring waits on that gate (TODO §0.2a).
 *
 *  TDFLite SAFETY (the acceptance gate): the actor fields are COP-internal. They
 *  NEVER reach the JWT / KAS subject / @opentdf/sdk rewrap, and the cap is purely
 *  SUBTRACTIVE — it can only LOWER what an actor sees (like the down-filter), never
 *  release something the KAS would deny. So lockstep stays 40/40 and the COP↔TDFLite
 *  contract is byte-identical. */

import type { ActorClass } from "../../../../packages/contracts-ts/src/index.ts";

/** Reserved namespace prefix for actor-model keys carried as runtime claims, so an
 *  actor field can never be mistaken for a bound policy-bundle attribute. */
export const SCOP_ACTOR_PREFIX = "scop:";

/** The recognized actor classes (fail-closed on anything else). */
export const ACTOR_CLASSES: readonly ActorClass[] = ["human", "npe", "ai-agent"];

/** Sentinel BELOW every real clearance order ⇒ "nothing visible" (fail-closed).
 *  Consumers compare `available = LEVELS.filter(l => CLASS_ORDER[l] <= cap)`, so a
 *  cap of -1 yields an empty available set. */
export const DENY_ORDER = -1;

/** The subtractive terms that can lower an actor's effective cap. Each is a class
 *  ORDER (an index into the clearance ordering; higher = more authority). Omit a
 *  term that does not apply (the default human path supplies none). */
export interface ActorCapTerms {
  /** The actor's nature; absent ⇒ treated as `human` (the legacy path). */
  actorClass?: ActorClass;
  /** Class-order of the principal an agent acts FOR (delegation cap). */
  onBehalfOfOrder?: number;
  /** Class-order cap of the NPE / path the actor talks THROUGH. */
  npeOrder?: number;
}

/** The effective class-order cap = min(clearance, every present subtractive term).
 *
 *  - Default human path (no terms) ⇒ returns `clearanceOrder` UNCHANGED (identity):
 *    this is what keeps lockstep at 40/40 by construction.
 *  - Every added term can only LOWER the cap, never raise it (the Glomar-safe
 *    direction — guards the one cap-widening bug class).
 *  - FAIL-CLOSED ⇒ `DENY_ORDER` on an unknown actor class or an unparseable
 *    (non-finite) delegation/NPE term (AOA §1: an unparseable on-behalf-of chain
 *    is denied, not defaulted-open). */
export function effectiveCap(clearanceOrder: number, terms: ActorCapTerms = {}): number {
  if (terms.actorClass !== undefined && !ACTOR_CLASSES.includes(terms.actorClass)) return DENY_ORDER;
  let cap = clearanceOrder;
  for (const term of [terms.onBehalfOfOrder, terms.npeOrder]) {
    if (term === undefined) continue;
    if (!Number.isFinite(term)) return DENY_ORDER; // unparseable cap ⇒ deny
    cap = Math.min(cap, term);
  }
  return cap;
}

/** Minimal bundle-attribute shape for the collision guard. */
interface AttrLike {
  name?: unknown;
}

/** Fail CLOSED if the live policy bundle ever declares an attribute whose name
 *  collides with the reserved actor prefix — otherwise an actor field carried in the
 *  subject record could be read as a bound ABAC attribute. Call at PDP construction
 *  (AOA §6; the adversarial-review axis-1 mitigation). */
export function assertNoBundleCollision(
  bundle: { attributes?: AttrLike[] } | null | undefined,
): void {
  for (const attr of bundle?.attributes ?? []) {
    if (typeof attr?.name === "string" && attr.name.startsWith(SCOP_ACTOR_PREFIX)) {
      throw new Error(
        `policy bundle attribute "${attr.name}" collides with the reserved actor prefix ` +
          `"${SCOP_ACTOR_PREFIX}" — an actor-model field could be read as a bound attribute (fail-closed)`,
      );
    }
  }
}
